import React, { useContext, useState } from "react";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import DataTable from "../../components/Elements/DataTable/DataTable";
import MainButton from "../../components/Elements/MainButton/MainButton";

import AdminCreateNotificationModal from "../../components/AdminPages/AdminCreateNotificationModal";
import AdminDeleteNotificationModal from "../../components/AdminPages/AdminDeleteNotificationModal";
import AdminEditNotificationModal from "../../components/AdminPages/AdminEditNotificationModal";

import { AuthContext } from "../../context/AuthContext";
import { fetchNotificationsAdmin } from "../../services/NotificationService";

import editIcon from "../../assets/icons/data/edit.png";
import deleteIcon from "../../assets/icons/data/delete.png";

// Columns always visible in notifications table
const alwaysVisibleColumns = [
  { key: "name", label: "Name", width: "320px" },
  { key: "type", label: "Type", width: "140px" },
  { key: "user_id", label: "User", width: "300px" },
  { key: "seen", label: "Seen", width: "140px" },
  { key: "created_at", label: "Created", width: "300px" },
];

// Extra columns user can toggle
const optionalColumns = [
  { key: "id", label: "ID", width: "320px" },
  { key: "description", label: "Description", width: "320px" },
];

// Sorting disabled via showSort=false, keep here for future
const sortOptions = [
  { value: "created_at", label: "Created At" },
  { value: "name", label: "Name" },
  { value: "type", label: "Type" },
  { value: "user_id", label: "User" },
  { value: "seen", label: "Seen" },
  { value: "id", label: "ID" },
  { value: "description", label: "Description" },
];

// Filters configuration
const filterDefinitions = [
  { key: "id", label: "ID", type: "text" },

  { key: "name", type: "text" },
  { key: "description", type: "text" },
  {
    key: "type",
    type: "select",
    options: [
      { value: "ok", label: "OK" },
      { value: "warning", label: "Warning" },
      { value: "error", label: "Error" },
      { value: "payment", label: "Payment" },
      { value: "topup", label: "Topup" },
      { value: "mail", label: "Mail" },
      { value: "settings", label: "Settings" },
      { value: "advertisement", label: "Advertisement" },
      { value: "statistic", label: "Statistic" },
    ],
  },
  {
    key: "seen",
    type: "select",
    options: [
      { value: "true", label: "Yes" },
      { value: "false", label: "No" },
    ],
  },
  { key: "user_id", type: "text" },
  {
    key: "created_at",
    label: "Created",
    type: "date-range",
    minKey: "created_at_min",
    maxKey: "created_at_max",
  },
];

// Custom cell renderers for notifications table
const notificationRenderers = {
  created_at: (item) => {
    if (!item.created_at) return "-";
    return new Date(item.created_at).toLocaleString("pl-PL");
  },
  type: (item) => {
    const types = {
      ok: "OK",
      warning: "Warning",
      error: "Error",
      payment: "Payment",
      topup: "Topup",
      mail: "Mail",
      settings: "Settings",
      advertisement: "Advertisement",
      statistic: "Statistic",
    };
    return types[item.type] || item.type || "-";
  },
  seen: (item) => (item.seen ? "Yes" : "No"),
};

const AdminNotifications = () => {
  const { token } = useContext(AuthContext);

  // Modal visibility and data version state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [dataVersion, setDataVersion] = useState(0);

  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [editingNotification, setEditingNotification] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Row action handlers
  const handleEditNotification = (notification) => {
    setEditingNotification(notification);
    setShowEditModal(true);
  };

  const handleNotificationUpdated = () => {
    setShowEditModal(false);
    setEditingNotification(null);
    setDataVersion((v) => v + 1);
  };

  const handleDeleteNotification = (notification) => {
    setSelectedNotification(notification);
    setShowDeleteModal(true);
  };

  const handleNotificationDeleted = () => {
    setShowDeleteModal(false);
    setSelectedNotification(null);
    setDataVersion((v) => v + 1);
  };

  // Data loader adapter for DataTable
  const fetchDataFn = ({ skip, limit, filters, sort_by, sort_order }) =>
    fetchNotificationsAdmin({
      offset: skip,
      limit,
      filters,
      sort_by,
      sort_order,
    });

  const handleNotificationCreated = () => {
    setShowCreateModal(false);
    setDataVersion((v) => v + 1);
  };

  const createButton = <MainButton onClick={() => setShowCreateModal(true)}>Create +</MainButton>;

  const notificationRowActions = [
    {
      label: "Edit",
      icon: editIcon,
      onClick: handleEditNotification,
    },
    {
      label: "Delete",
      icon: deleteIcon,
      onClick: handleDeleteNotification,
    },
  ];

  return (
    <div className="admin-notifications-page">
      <PageHeader title="Notifications" description="View all notifications" actions={createButton} />

      <DataTable
        key={dataVersion}
        tableId="admin-notifications"
        fetchDataFunction={fetchDataFn}
        columns={alwaysVisibleColumns}
        optionalColumns={optionalColumns}
        sortOptions={sortOptions}
        filterDefinitions={filterDefinitions}
        customRenderers={notificationRenderers}
        defaultSortBy={null}
        itemKey="id"
        rowActions={notificationRowActions}
        showCheckboxes={false}
        showFilters={true}
        showPageSize={true}
        showSort={true}
      />

      <AdminCreateNotificationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        token={token}
        onCreated={handleNotificationCreated}
      />

      <AdminDeleteNotificationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        notification={selectedNotification}
        onDeleted={handleNotificationDeleted}
      />

      <AdminEditNotificationModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        notification={editingNotification}
        onUpdated={handleNotificationUpdated}
      />
    </div>
  );
};

export default AdminNotifications;
